import { useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';

import { AuthContext, type AuthState } from '@/app/auth/context';
import * as authApi from '@/features/auth/api';
import type { CurrentUser, Role } from '@/features/auth/api';
import { ApiError } from '@/lib/api/http';

/**
 * セッション状態の保持。
 * 画面はこれを見てメニューを出し分けるが、**認可の本体は API 側**（指示書 §0-6）。
 */
export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setUser(await authApi.fetchMe());
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setUser(null);
      } else {
        throw error;
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const changeRole = useCallback(async (role: Role) => {
    setUser(await authApi.switchRole(role));
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      isLoading,
      needsTotp: user !== null && user.totp_required && !user.totp_verified,
      refresh,
      signOut,
      changeRole,
      setUser,
    }),
    [user, isLoading, refresh, signOut, changeRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
